<template>
  <div v-if="visible" class="saud-root" :class="['saud-side-' + side, { 'saud-is-mobile': mobile }]" dir="rtl">
    <transition name="saud-pop">
      <section v-if="open" class="saud-panel" role="dialog" :aria-label="title" :style="panelStyle" @keydown.esc="open = false">
        <header class="saud-head">
          <span class="saud-head-av"><img src="/logo.png" alt=""></span>
          <div class="saud-head-text">
            <div class="saud-head-name">
              <strong>{{ cfg.name || 'سعود' }}</strong>
              <span class="saud-quota" :class="{ 'saud-quota-low': remaining <= 5 }" title="الرسائل المتبقية لمدرستك اليوم">
                متبقي {{ remaining }} من {{ limit }} اليوم
              </span>
            </div>
            <small>{{ cfg.subtitle || 'المساعد الذكي بالذكاء الاصطناعي' }}</small>
          </div>
          <button type="button" class="saud-ic" title="محادثة جديدة" aria-label="محادثة جديدة" :disabled="busy" @click="reset()"><i class="fa fa-refresh"></i></button>
          <button type="button" class="saud-ic" title="إغلاق" aria-label="إغلاق" @click="open = false"><i class="fa" :class="mobile ? 'fa-chevron-down' : 'fa-times'"></i></button>
        </header>

        <div ref="body" class="saud-body" aria-live="polite">
          <div class="saud-msg saud-saud">
            <span class="saud-av"><img src="/logo.png" alt=""></span>
            <div class="saud-col">
              <div class="saud-bubble" v-html="fmt(cfg.welcome || WELCOME)"></div>
            </div>
          </div>
          <div v-if="!messages.length && prompts.length" class="saud-prompts">
            <button v-for="q in prompts" :key="q" type="button" class="saud-chip" :disabled="busy || remaining <= 0" @click="send(q)">
              <i class="fa fa-magic"></i> {{ q }}
            </button>
          </div>

          <div v-for="m in messages" :key="m.id" class="saud-msg" :class="m.role == 'user' ? 'saud-user' : 'saud-saud'">
            <span v-if="m.role != 'user'" class="saud-av"><img src="/logo.png" alt=""></span>
            <div class="saud-col">
              <div class="saud-bubble" :class="{ 'saud-bubble-err': m.error }" v-html="fmt(m.text)"></div>
              <div v-if="m.shot" class="saud-tag"><i class="fa fa-camera"></i> {{ m.shotFailed ? 'تعذّر إرفاق اللقطة' : 'مع لقطة من الصفحة' }}</div>
              <button v-if="m.error && m.retryOf" type="button" class="saud-retry" :disabled="busy" @click="retry(m)">
                <i class="fa fa-repeat"></i> إعادة المحاولة
              </button>
              <div v-for="(a, i) in m.actions" v-if="allowed(a)" :key="i" class="saud-card" :class="'saud-card-' + a.type">
                <div class="saud-card-row">
                  <span class="saud-card-ic"><i class="fa" :class="iconOf(a)"></i></span>
                  <div class="saud-card-main">
                    <b>{{ a.title || a.label || KINDS[a.type] || 'تصميم' }}</b>
                    <small v-if="a.type == 'open_page'"><bdi dir="ltr">{{ a.route }}</bdi></small>
                    <small v-else>{{ metaOf(m, a) }}</small>
                  </div>
                </div>
                <img v-if="previews[m.id + ':' + i]" class="saud-card-prev" :src="previews[m.id + ':' + i]" alt="معاينة الصورة">
                <div v-if="a._msg" class="saud-card-note" :class="'saud-note-' + a._state">
                  <i class="fa" :class="a._state == 'busy' ? 'fa-spinner fa-spin' : a._state == 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'"></i>
                  {{ a._msg }}
                </div>
                <!-- «كل البيانات»: fetching every page of the list, with a cancel button -->
                <div v-if="a._state == 'busy' && a._fetch" class="saud-prog-row">
                  <div class="saud-prog" :class="{ 'saud-prog-indet': a._prog == null }">
                    <i :style="a._prog == null ? {} : { width: Math.round(a._prog * 100) + '%' }"></i>
                  </div>
                  <button type="button" class="saud-cancel" @click="cancel(m, i)"><i class="fa fa-stop-circle"></i> إلغاء</button>
                </div>
                <!-- export scope «ask»: the page shows only part of the rows → the school chooses, no new question -->
                <div v-if="choiceOf(m, a)" class="saud-scope">
                  <small class="saud-scope-q"><i class="fa fa-question-circle"></i> تصدير الصفحة الحالية أم كل البيانات؟</small>
                  <div class="saud-scope-btns">
                    <button type="button" class="saud-card-btn saud-scope-page" :disabled="a._state == 'busy'" @click="run(m, i, 'page')">
                      <i class="fa fa-file-text-o"></i> الصفحة الحالية ({{ choiceOf(m, a).shown }})
                    </button>
                    <button type="button" class="saud-card-btn saud-scope-all" :disabled="a._state == 'busy'" @click="run(m, i, 'all')">
                      <i class="fa fa-database"></i> كل البيانات<template v-if="choiceOf(m, a).total"> ({{ choiceOf(m, a).total }})</template>
                    </button>
                  </div>
                </div>
                <button v-else type="button" class="saud-card-btn" :disabled="a._state == 'busy'" @click="run(m, i)">
                  <i class="fa" :class="a._state == 'busy' ? 'fa-spinner fa-spin' : btnIcon(a)"></i>
                  {{ a._state == 'done' && a.type != 'open_page' && a.type != 'chart' ? 'تحميل مرة أخرى' : btnLabel(a) }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="busy" class="saud-msg saud-saud">
            <span class="saud-av"><img src="/logo.png" alt=""></span>
            <div class="saud-col">
              <div class="saud-bubble saud-typing" aria-label="سعود يكتب"><i></i><i></i><i></i></div>
              <small v-if="busyNote" class="saud-busy-note">{{ busyNote }}</small>
            </div>
          </div>
        </div>

        <footer class="saud-foot">
          <div v-if="remaining <= 0" class="saud-limit"><i class="fa fa-clock-o"></i> وصلت للحد اليومي ({{ limit }} رسالة). يتجدد الحد غداً.</div>
          <label v-if="cfg.screenshot && remaining > 0" class="saud-attach" :class="{ 'saud-attach-on': attach }" title="ترسل صورة مصغّرة من الجزء الظاهر من الصفحة مع رسالتك التالية فقط">
            <input v-model="attach" type="checkbox"> <i class="fa fa-camera"></i> إرفاق لقطة من الصفحة
          </label>
          <div class="saud-input">
            <textarea ref="input" v-model="draft" rows="1" :maxlength="maxChars" :disabled="remaining <= 0"
                      placeholder="اكتب سؤالك أو اطلب ملفاً…" aria-label="رسالتك إلى سعود"
                      @keydown.enter.exact.prevent="send()" @input="grow"></textarea>
            <button type="button" class="saud-send" :disabled="!canSend" aria-label="إرسال" @click="send()">
              <i class="fa fa-paper-plane"></i>
            </button>
          </div>
          <small class="saud-privacy"><i class="fa fa-lock"></i> يقرأ سعود ملخص الصفحة بدون الأسماء والأرقام الشخصية.</small>
        </footer>
      </section>
    </transition>

    <!-- on phones the open sheet has its own close button: the round button would cover the send button -->
    <button v-show="!(mobile && open)" type="button" class="saud-fab" :class="{ 'saud-fab-on': open }" :style="fabStyle" :aria-expanded="open ? 'true' : 'false'"
            :aria-label="title" @click="toggle()">
      <img v-if="!open" src="/logo.png" alt="">
      <i v-else class="fa fa-times"></i>
      <span v-if="!open" class="saud-fab-ai">AI</span>
      <span class="saud-fab-tip">{{ title }}</span>
    </button>

    <b-modal v-model="chart.show" :title="chart.title" size="lg" hide-footer centered modal-class="saud-chart-modal" body-class="saud-chart-body" @shown="chartShown()">
      <!-- apexcharts lays out its SVG labels for ltr; Arabic text still reads right-to-left inside them -->
      <div dir="ltr">
        <apexchart v-if="chart.show" ref="apex" :type="chart.type" :options="chart.options" :series="chart.series" :height="chart.height" />
      </div>
      <div class="text-center mt-1">
        <button type="button" class="btn btn-primary" @click="chartPng()"><i class="fa fa-download"></i> تحميل الرسم PNG</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue';
import { call, jwt, upload } from './saud-api';
import { buildSnapshot, extractTables } from './snapshot';
import { BUTTONS, KINDS, findTable, makeExcel, makeDocument, chartData, openPage, pageScreenshot, downloadDataUrl, safeName } from './actions';
import { makeDesign } from './design';
import { fetchAllRows, MAX_ROWS } from './adapters';

// «سعود — المساعد الذكي بالذكاء الاصطناعي»: a floating button bottom-left on every school page (stacked above the
// «الدعم الفني» button when it is shown) and its chat panel (a full-screen sheet on phones).
// Never in the teacher area, never on login / public / short-link pages; nothing at all when the server says
// enabled:false. The conversation stays in this tab (sessionStorage); the page rows used for files stay in memory.

var EXACT = ['/login', '/logout', '/register', '/reset', '/activate', '/error-404', '/system_stopped', '/expired', '/NotPermitted', '/update-count', '/subscribe'];
var PREFIX = ['/_xteacher', '/autologin', '/s/', '/reply-to-ghiab', '/_students/reply-to-ghiab', '/teacher-ask/', '/mail/', '/result/', '/weekplan/',
  '/parent-student-full-report', '/que/', '/que2/', '/gque2/', '/comm-attend', '/find-comm', '/results-primary-link/', '/ticket/'];
var STORE = 'saud_chat_v1';
var CFG = 'saud_cfg_v2';
var CFG_TTL = 60 * 1000;           // the merged config of a page (per route) is asked again after a minute
// admin switches (actions.<key>) for each action type
var SWITCH = { excel: 'excel', pdf: 'pdf', image: 'image', chart: 'chart', open_page: 'open_page', design: 'custom_design' };
var JOBS = {};     // running «كل البيانات» fetches: card key → cancel()
var WELCOME = 'أهلاً! أنا سعود، المساعد الذكي بالذكاء الاصطناعي. اسألني عن أي شيء في التحضير الذكي، أو اطلب مني ملف Excel أو PDF أو صورة من هذه الصفحة.';
var TABLES = {};   // user message id → the page tables (full rows, personal values included) — memory only

function hash(s){
  var h = 5381;
  for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return String(h);
}

function readStore(key){
  try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { return null; }
}
function writeStore(key, v){
  try { sessionStorage.setItem(key, JSON.stringify(v)); } catch (e) { /* private mode / full */ }
}

function uid(){
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function esc(s){
  return String(s == null ? '' : s).replace(/[&<>"']/g, function(c){ return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
}

export default {
  components: {
    BModal,
    apexchart: function(){ return import(/* webpackChunkName: "saud-chart" */ 'vue-apexcharts').then(function(m){ return m.default || m; }); },
  },
  data(){
    var saved = readStore(STORE) || {};
    return {
      path: window.location.pathname,
      who: '',
      cfg: { enabled: false },
      cfgAt: 0,
      sideOffset: 20,
      cfgFailedAt: 0,
      open: false,
      mobile: window.innerWidth <= 576,
      fabBottom: 20,
      messages: Array.isArray(saved.messages) ? saved.messages : [],
      draft: '',
      busy: false,
      busyNote: '',
      attach: false,
      remaining: 0,
      limit: 50,
      previews: {},
      chart: { show: false, title: '', type: 'bar', options: {}, series: [], height: 380 },
      WELCOME: WELCOME, BUTTONS: BUTTONS, KINDS: KINDS,
    };
  },
  computed: {
    title(){
      return (this.cfg.name || 'سعود') + ' — المساعد الذكي';
    },
    routeOk(){
      var p = this.path;
      if (!this.who) return false;
      if (p.indexOf('xteacher') >= 0) return false;
      if (this.$route && this.$route.meta && this.$route.meta.layout == 'full') return false;
      if (EXACT.indexOf(p.replace(/\/+$/, '') || '/') >= 0) return false;
      return !PREFIX.some(function(h){ return p.indexOf(h) === 0; });
    },
    visible(){
      // enabled = this school, this page (page rule) and this hour (all decided by the server for the route)
      return this.routeOk && !!this.cfg.enabled && !(this.mobile && this.cfg.show_on_mobile === false);
    },
    side(){
      return this.cfg.position == 'right' ? 'right' : 'left';
    },
    maxChars(){
      var n = parseInt(this.cfg.max_message_chars, 10);
      return n >= 100 && n <= 2000 ? n : 1000;
    },
    allowAll(){
      return this.cfg.allow_all_data !== false;
    },
    prompts(){
      return (Array.isArray(this.cfg.quick_prompts) ? this.cfg.quick_prompts : []).filter(Boolean).slice(0, 6);
    },
    canSend(){
      return !this.busy && this.remaining > 0 && this.draft.trim().length > 0;
    },
    fabStyle(){
      var st = { bottom: this.fabBottom + 'px' };
      st[this.side] = this.sideOffset + 'px';
      return st;
    },
    panelStyle(){
      if (this.mobile) return {};
      var bottom = this.fabBottom + 70;
      var st = { bottom: bottom + 'px', maxHeight: 'calc(100vh - ' + (bottom + 16) + 'px)' };
      st[this.side] = this.sideOffset + 'px';
      return st;
    },
  },
  watch: {
    $route(){
      this.path = window.location.pathname;
      var who = jwt() ? hash(jwt()) : '';
      if (who != this.who && this.who){
        // another account in this tab: nothing of the previous one stays
        this.reset();
        this.cfg = { enabled: false };
        this.cfgAt = 0;
        this.cfgCache = {};
        this.open = false;
      }
      this.who = who;
      this.loadConfig();
      this.restackSoon();
    },
    open(v){
      var g = this;
      this.persist();
      this.lockBody(v && this.mobile);
      if (v){
        if (Date.now() - this.cfgAt > CFG_TTL) this.loadConfig(true);
        this.$nextTick(function(){
          g.scrollDown();
          if (!g.mobile && g.$refs.input) g.$refs.input.focus();
        });
      }
    },
    mobile(v){
      this.lockBody(v && this.open);
    },
    messages(){
      this.persist();
    },
  },
  created(){
    // the chart in the modal: its message / action / scope (not reactive), for chartShown
    this.chartFor = null;
    var saved = readStore(STORE) || {};
    this.who = jwt() ? hash(jwt()) : '';
    // another school signed in in this tab: forget the old conversation
    if (saved.who && saved.who != this.who) this.messages = [];
    // the merged config of each page (per route), kept for this tab
    var c = readStore(CFG);
    this.cfgCache = c && c.who == this.who && c.routes ? c.routes : {};
    this.open = !!saved.open && this.messages.length > 0 && window.innerWidth > 576;
    this.loadConfig();
  },
  mounted(){
    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    this.restackSoon();
  },
  beforeDestroy(){
    window.removeEventListener('resize', this.onResize);
    this.lockBody(false);
  },
  methods: {
    // /user/saud/config {route}: the server merges school / page rule / hours / switches for this page.
    // Cached per route for a minute; a page seen before shows its last config at once while it is asked again.
    loadConfig(force){
      var g = this;
      if (!this.who || !this.routeOk) return;
      var route = this.path;
      var c = this.cfgCache[route];
      if (c && c.data){
        if (c.at != this.cfgAt || this.cfgRoute != route) this.applyConfig(c.data, c.at, route);
        if (!force && Date.now() - c.at < CFG_TTL) return;
      }
      if (!force && !c && this.cfgFailedAt && Date.now() - this.cfgFailedAt < 30 * 1000) return;
      if (this.cfgLoading == route) return;
      this.cfgLoading = route;
      call('/config', { route: route }, 15000).then(function(r){
        if (g.cfgLoading == route) g.cfgLoading = null;
        if (!r.ok || !r.data){
          // keep a config we already have when the network blinks; otherwise stay hidden
          g.cfgFailedAt = Date.now();
          if (!g.cfgAt) g.cfg = { enabled: false };
          return;
        }
        g.cfgCache[route] = { at: Date.now(), data: r.data };
        var keys = Object.keys(g.cfgCache);
        if (keys.length > 40) keys.sort(function(a, b){ return g.cfgCache[a].at - g.cfgCache[b].at; }).slice(0, keys.length - 40).forEach(function(k){ delete g.cfgCache[k]; });
        writeStore(CFG, { who: g.who, routes: g.cfgCache });
        if (route == g.path) g.applyConfig(r.data, g.cfgCache[route].at, route);
      });
    },
    applyConfig(d, at, route){
      this.cfg = Object.assign({ enabled: false }, d);
      this.cfgAt = at;
      this.cfgRoute = route;
      this.cfgWho = this.who;
      this.cfgFailedAt = 0;
      this.limit = parseInt(d.limit, 10) || 50;
      this.remaining = d.remaining == null ? this.limit : Math.max(0, parseInt(d.remaining, 10) || 0);
      if (!this.cfg.enabled) this.open = false;
      this.restackSoon();
    },
    onResize(){
      this.mobile = window.innerWidth <= 576;
      this.restackSoon();
    },
    // above the «الدعم الفني» button when that one is on the page (no overlap)
    restackSoon(){
      var g = this;
      clearTimeout(this.stackTimer);
      this.stackTimer = setTimeout(function(){
        var base = g.mobile ? 16 : 20;
        // «الدعم الفني» sits bottom-left: stack above it only on the same side
        var sf = g.side == 'left' && document.querySelector('.sf .sf-fab');
        if (sf && sf.getClientRects().length){
          var r = sf.getBoundingClientRect();
          g.fabBottom = Math.max(base, Math.round(window.innerHeight - r.top) + 12);
        } else g.fabBottom = base;
        // on the right, the side menu (RTL) is there on wide screens: stay just beside it, never over it
        var off = base;
        var menu = g.side == 'right' && !g.mobile && document.querySelector('.main-menu');
        if (menu && menu.getClientRects().length){
          var mr = menu.getBoundingClientRect();
          if (mr.width > 0 && mr.left > window.innerWidth / 2 && mr.left < window.innerWidth - 10) off = Math.round(window.innerWidth - mr.left) + base;
        }
        g.sideOffset = off;
      }, 350);
    },
    lockBody(on){
      try { document.body.classList.toggle('saud-lock', !!on); } catch (e) { /* no body */ }
    },
    persist(){
      var keep = this.messages.slice(-40).map(function(m){
        // previews and page rows never go to storage
        return { id: m.id, role: m.role, text: m.text, at: m.at, route: m.route, error: m.error || undefined, retryOf: m.retryOf || undefined,
          shot: m.shot || undefined, shotFailed: m.shotFailed || undefined, ask: m.ask || undefined, tables: m.tables || undefined, log: m.log || undefined,
          actions: (m.actions || []).map(function(a){ var c = Object.assign({}, a); if (c._state == 'busy'){ c._state = 'idle'; c._msg = ''; } return c; }) };
      });
      writeStore(STORE, { who: this.who, open: this.open, messages: keep });
    },
    toggle(){
      this.open = !this.open;
    },
    reset(){
      this.messages = [];
      this.previews = {};
      TABLES = {};
      this.draft = '';
      this.persist();
    },
    grow(){
      var el = this.$refs.input;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(120, el.scrollHeight) + 'px';
    },
    scrollDown(){
      var b = this.$refs.body;
      if (b) b.scrollTop = b.scrollHeight;
    },
    later(){
      var g = this;
      this.$nextTick(function(){ g.scrollDown(); });
    },
    fmt(t){
      // escaped text; **bold**, «- » bullets, line breaks
      var lines = esc(t || '').split(/\n/);
      var html = '', list = false;
      lines.forEach(function(l){
        var b = /^\s*(?:[-•*]|\d+[.)])\s+(.*)$/.exec(l);
        if (b){
          if (!list){ html += '<ul>'; list = true; }
          html += '<li>' + b[1] + '</li>';
        } else {
          if (list){ html += '</ul>'; list = false; }
          html += (html && !/<\/ul>$/.test(html) ? '<br>' : '') + l;
        }
      });
      if (list) html += '</ul>';
      return html.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
    },
    // an action the admin switched off (actions.<key> = false) is not shown
    allowed(a){
      var sw = this.cfg.actions || {};
      return sw[SWITCH[a.type]] !== false;
    },
    // scope «ask» on a table that shows only part of its rows → { shown, total|null }; else null (one button)
    choiceOf(m, a){
      if (a.type == 'open_page' || !this.allowAll) return null;
      if (a.scope && a.scope != 'ask') return null;
      var t = m.tables && m.tables[a.table];
      if (!t || !t.more) return null;
      return { shown: t.shown != null ? t.shown : t.rows, total: t.total || null };
    },
    btnLabel(a){
      if (a.type == 'design') return a.format == 'pdf' ? 'تحميل PDF' : 'حفظ الصورة';
      return BUTTONS[a.type] || 'تنفيذ';
    },
    cancel(m, i){
      var job = JOBS[m.id + ':' + i];
      if (job) job();
    },
    iconOf(a){
      return { excel: 'fa-file-excel-o', pdf: 'fa-file-pdf-o', image: 'fa-picture-o', open_page: 'fa-external-link', design: 'fa-paint-brush',
        chart: a.chart == 'pie' ? 'fa-pie-chart' : a.chart == 'line' ? 'fa-line-chart' : 'fa-bar-chart' }[a.type] || 'fa-file-o';
    },
    btnIcon(a){
      return { excel: 'fa-download', pdf: 'fa-download', image: 'fa-download', design: 'fa-download', chart: 'fa-eye', open_page: 'fa-arrow-left' }[a.type] || 'fa-play';
    },
    metaOf(m, a){
      if (a.type == 'open_page') return a.route || '';
      var t = m.tables && m.tables[a.table];
      var label = function(k){ return t && t.cols && t.cols[k] || k; };
      var parts = [];
      if (t) parts.push((t.title || 'الجدول') + (t.shown != null ? ' (' + t.shown + (t.more ? (t.total ? ' من ' + t.total : '+') : '') + ' صف)' : t.rows != null ? ' (' + t.rows + ' صف)' : ''));
      if (a.type == 'design') parts.push('تصميم ' + (a.format == 'pdf' ? 'PDF' : 'صورة'));
      if (a.scope == 'all') parts.push('كل البيانات');
      else if (a.scope == 'page' && t && t.more) parts.push('الصفحة الحالية');
      if (a.type == 'chart'){
        parts.push({ bar: 'أعمدة', pie: 'دائري', line: 'خطي' }[a.chart] || 'رسم');
        if (a.x) parts.push('حسب ' + label(a.x));
        return parts.join(' · ');
      }
      if (Array.isArray(a.columns) && a.columns.length) parts.push(a.columns.length + ' أعمدة');
      if (a.filter && a.filter.key) parts.push(label(a.filter.key) + ': ' + (Array.isArray(a.filter.value) ? a.filter.value.join('، ') : a.filter.value));
      if (a.group_by) parts.push('مجمّع حسب ' + label(a.group_by));
      if (a.sort && a.sort.key) parts.push('مرتب حسب ' + label(a.sort.key));
      if (a.type == 'pdf' || a.type == 'image') parts.push({ cards: 'بطاقات', summary: 'ملخص' }[a.template] || 'جدول');
      return parts.join(' · ');
    },
    // the last messages BEFORE this question (errors left out): history_turns from the admin (0..10, default 6)
    history(u){
      var idx = this.messages.indexOf(u);
      var n = parseInt(this.cfg.history_turns, 10);
      n = isNaN(n) ? 6 : Math.max(0, Math.min(10, n));
      if (!n) return [];
      return this.messages.slice(0, idx < 0 ? this.messages.length : idx).filter(function(m){ return !m.error && m.text; }).slice(-n)
        .map(function(m){ return { role: m.role == 'user' ? 'user' : 'saud', text: String(m.text).slice(0, 600) }; });
    },
    send(text){
      text = String(text == null ? this.draft : text).trim();
      if (!text || this.busy || this.remaining <= 0) return;
      var u = { id: uid(), role: 'user', text: text.slice(0, 1000), at: Date.now(), route: window.location.pathname, shot: !!(this.attach && this.cfg.screenshot) };
      this.messages.push(u);
      this.draft = '';
      this.$nextTick(this.grow);
      this.ask(u);
    },
    retry(m){
      var u = this.messages.filter(function(x){ return x.id == m.retryOf; })[0];
      if (!u || this.busy) return;
      this.messages = this.messages.filter(function(x){ return x.id != m.id; });
      this.ask(u);
    },
    ask(u){
      var g = this;
      this.busy = true;
      this.busyNote = '';
      this.later();
      var snap;
      try { snap = buildSnapshot(); } catch (e) { snap = null; }
      if (!snap) snap = { json: JSON.stringify({ route: window.location.pathname, title: '', filters: {}, cards: [], tables: [], texts: [] }), snapshot: { tables: [] }, tables: [] };
      TABLES[u.id] = snap.tables;
      var labels = {};
      // per table (labels and counts only, no values): card texts, the scope choice, finding the table again
      (snap.snapshot.tables || []).forEach(function(t){
        var ext = findTable(snap.tables, t.id) || { columns: t.columns, rows: [] };
        var cols = {};
        ext.columns.forEach(function(c){ cols[c.key] = c.label; });
        labels[t.id] = { title: t.title, rows: t.row_count, shown: ext.rows.length, total: ext.total != null ? ext.total : null,
          more: !!ext.more, cols: cols };
      });
      var withShot = u.shot;
      this.attach = false;
      var shotP = withShot ? (this.busyNote = 'جاري تجهيز لقطة الصفحة…', pageScreenshot().catch(function(){ return null; })) : Promise.resolve(null);
      shotP.then(function(shot){
        if (withShot && !shot) g.$set(u, 'shotFailed', true);
        g.busyNote = '';
        var data = { message: u.text, page: snap.json, route: u.route };
        var h = g.history(u);
        if (h.length) data.history = h;
        if (shot) data.screenshot = shot;
        return call('/chat', data, 70000);
      }).then(function(r){
        g.busy = false;
        if (!r.ok){
          g.messages.push({ id: uid(), role: 'saud', error: true, text: r.message, retryOf: u.id, at: Date.now(), route: u.route });
          g.later();
          return;
        }
        var d = r.data || {};
        if (d.limit != null) g.limit = parseInt(d.limit, 10) || g.limit;
        if (d.remaining != null) g.remaining = Math.max(0, parseInt(d.remaining, 10) || 0);
        var actions = (Array.isArray(d.actions) ? d.actions : []).filter(function(a){ return a && (BUTTONS[a.type] || a.type == 'design'); }).slice(0, 6)
          .map(function(a){ return Object.assign({}, a, { _state: 'idle', _msg: '' }); });
        // log: the server's log of this answer, the files built from it are copied there (keepFile)
        g.messages.push({ id: uid(), role: 'saud', text: String(d.text || (actions.length ? 'تفضل، جهّزت لك التالي:' : 'لم أفهم طلبك جيداً، جرّب صياغة أخرى.')),
          actions: actions, ask: u.id, tables: labels, at: Date.now(), route: u.route, log: /^[0-9a-f]{24}$/.test(d.log_id || '') ? d.log_id : undefined });
        g.later();
      });
    },
    // the table an action points at: the rows read when the question was sent, else the page again (same page only)
    tableFor(m, id){
      var t = findTable(TABLES[m.ask], id);
      if (t) return t;
      if (window.location.pathname != m.route) return null;
      var want = m.tables && m.tables[id];
      var same = function(x){
        if (!want || !want.cols) return true;
        return Object.keys(want.cols).every(function(k){ return x.columns.some(function(c){ return c.key == k && c.label == want.cols[k]; }); });
      };
      var live = [];
      try { live = extractTables(); } catch (e) { live = []; }
      t = findTable(live, id);
      if (t && same(t)) return t;
      return live.filter(same)[0] || null;
    },
    // scope: 'page' | 'all' from the choice buttons; else the action's own scope ('all' only when allowed)
    run(m, i, scope){
      var g = this;
      var a = m.actions[i];
      if (!a || a._state == 'busy' || !this.allowed(a)) return;
      if (a.type == 'open_page'){
        var o = openPage(a, this.$router);
        a._state = o.ok ? 'done' : 'error';
        a._msg = o.ok ? '' : o.message;
        if (o.ok && this.mobile) this.open = false;
        this.persist();
        return;
      }
      var table = this.tableFor(m, a.table);
      if (!table){
        a._state = 'error';
        a._msg = window.location.pathname != m.route
          ? 'هذا الطلب كان من صفحة أخرى. ارجع إليها واطلب مرة أخرى.'
          : 'الجدول المطلوب لم يعد ظاهراً في الصفحة. حدّث الصفحة واطلب مرة أخرى.';
        return;
      }
      var key = m.id + ':' + i;
      var pick = scope || (a.scope == 'all' ? 'all' : 'page');
      var note = '';
      if (pick == 'all' && !this.allowAll){ pick = 'page'; note = ' (تصدير كل البيانات غير مفعّل لهذه الصفحة)'; }
      var ctx = { school: this.cfg.school || {}, templates: this.cfg.templates || {}, onProgress: function(t){ a._msg = t; } };
      a._state = 'busy';
      a._msg = 'جاري التجهيز…';
      this.$set(a, '_fetch', false);
      this.$set(a, '_prog', null);

      // the rows: shown now, everything the page holds, or every page fetched by the page's adapter
      var rowsP;
      if (pick == 'all' && table.adapter && table.more){
        a._fetch = true;
        a._msg = 'جاري جلب كل البيانات…';
        var job = fetchAllRows(table.adapter, table, function(text, frac){ a._msg = text; a._prog = frac; });
        JOBS[key] = job.cancel;
        rowsP = job.promise.then(function(r){
          delete JOBS[key];
          a._fetch = false;
          if (!r.ok) return Promise.reject(r);
          if (r.capped) note += ' — تم الاكتفاء بأول ' + MAX_ROWS + ' صف';
          return r.rows;
        });
      } else {
        rowsP = Promise.resolve(pick == 'all' ? (table.allRows || table.rows) : table.rows);
      }
      rowsP.then(function(rows){
        var view = Object.assign({}, table, { rows: rows });
        a._msg = 'جاري التجهيز…';
        if (a.type == 'excel') return makeExcel(a, view, ctx);
        if (a.type == 'pdf' || a.type == 'image') return makeDocument(a, view, ctx);
        if (a.type == 'design') return makeDesign(a, view, ctx);
        if (a.type == 'chart'){
          var c = chartData(a, view, g.cfg.templates);
          if (c.ok){
            g.chartFor = { m: m, i: i, pick: pick };
            g.chart = { show: true, title: c.title, type: c.type, options: c.options, series: c.series, height: c.height };
            c = { ok: true, message: 'الرسم مفتوح الآن (' + rows.length + ' صف).' };
          }
          return c;
        }
        return { ok: false, message: 'هذا الإجراء غير متاح.' };
      }).catch(function(e){
        if (e && e.message && e.ok === false) return e;
        console.error('[saud] action', e);
        return { ok: false, message: 'تعذّر التنفيذ. حاول مرة أخرى.' };
      }).then(function(r){
        r = r || { ok: false };
        a._fetch = false;
        a._state = r.ok ? 'done' : 'error';
        a._msg = (r.message || (r.ok ? '' : 'تعذّر التنفيذ. حاول مرة أخرى.')) + (r.ok && note ? note : '');
        if (r.preview) g.$set(g.previews, key, r.preview);
        if (r.ok && r.file) g.keepFile(m, i, pick, r.file);
        g.persist();
      });
    },
    // a copy of a file built here → the answer's log, for the admin's «المحادثات»: once per action and scope,
    // in the background after the download (saud-api.js upload: failures ignored, over 8 MB skipped)
    keepFile(m, i, pick, file){
      var a = m.actions[i];
      if (!m.log || !a || !file || !file.blob) return;
      var done = String(a._kept || '').split(',');
      if (done.indexOf(pick) >= 0) return;
      a._kept = done.concat(pick).filter(Boolean).join(',');
      setTimeout(function(){ upload(m.log, file.kind, file.blob, file.name, i); }, 400);
    },
    // the chart is drawn (no animation) once the modal is shown: its PNG goes to the log like the other files
    chartShown(){
      var g = this, f = this.chartFor;
      if (!f || !f.m.log) return;
      setTimeout(function(){
        var c = g.$refs.apex && g.$refs.apex.chart;
        if (!g.chart.show || !c || !c.dataURI) return;
        c.dataURI().then(function(r){ return fetch(r.imgURI); }).then(function(x){ return x.blob(); }).then(function(b){
          g.keepFile(f.m, f.i, f.pick, { kind: 'image', blob: b, name: safeName(g.chart.title) + '.png' });
          g.persist();
        }).catch(function(){});
      }, 500);
    },
    chartPng(){
      var g = this;
      var c = this.$refs.apex && this.$refs.apex.chart;
      if (!c || !c.dataURI) return;
      c.dataURI().then(function(r){ downloadDataUrl(r.imgURI, safeName(g.chart.title) + '.png'); });
    },
  },
};
</script>

<style>
/* the app runs postcss-rtl (it mirrors left/right for [dir=rtl]); these rules are written for the real
   (physical) sides already, so they are left alone. Scoped under .saud-root to win over the app's [dir] rules. */
/* rtl:begin:ignore */
.saud-root { position: relative; z-index: 1039; font-family: inherit; }
.saud-lock { overflow: hidden !important; }

/* floating button */
.saud-root .saud-fab {
  position: fixed; z-index: 1039; width: 58px; height: 58px; padding: 0; border: 0; border-radius: 50%; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #1D4ED8, #7A5AF8 55%, #12B76A) border-box;
  border: 3px solid transparent; box-shadow: 0 10px 26px rgba(29, 78, 216, 0.32);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  animation: saud-pulse 2.2s ease-out 1s 2;
}
.saud-root .saud-fab:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(29, 78, 216, 0.4); }
.saud-root .saud-fab:focus-visible { outline: 3px solid rgba(122, 90, 248, 0.5); outline-offset: 3px; }
.saud-root .saud-fab img { width: 38px; height: 38px; object-fit: contain; }
.saud-root .saud-fab .fa { font-size: 22px; color: #1D4ED8; }
.saud-root .saud-fab-on { background: linear-gradient(135deg, #1D4ED8, #7A5AF8) padding-box, linear-gradient(135deg, #1D4ED8, #7A5AF8) border-box; }
.saud-root .saud-fab-on .fa { color: #fff; }
.saud-root .saud-fab-ai {
  position: absolute; top: -6px; right: -6px; min-width: 24px; height: 18px; padding: 0 5px; border-radius: 9px;
  background: linear-gradient(135deg, #7A5AF8, #1D4ED8); color: #fff; font: 800 10px/18px Arial, sans-serif; letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(122, 90, 248, 0.45);
}
.saud-root .saud-fab-tip {
  position: absolute; left: calc(100% + 12px); top: 50%; transform: translateY(-50%); white-space: nowrap; pointer-events: none;
  background: #0F1733; color: #fff; font-size: 12.5px; font-weight: 700; padding: 6px 12px; border-radius: 10px; opacity: 0;
  transition: opacity 0.15s ease;
}
.saud-root .saud-fab:hover .saud-fab-tip, .saud-root .saud-fab:focus-visible .saud-fab-tip { opacity: 1; }
.saud-root .saud-fab-on .saud-fab-tip { display: none; }
@keyframes saud-pulse {
  0% { box-shadow: 0 10px 26px rgba(29, 78, 216, 0.32), 0 0 0 0 rgba(122, 90, 248, 0.45); }
  70% { box-shadow: 0 10px 26px rgba(29, 78, 216, 0.32), 0 0 0 16px rgba(122, 90, 248, 0); }
  100% { box-shadow: 0 10px 26px rgba(29, 78, 216, 0.32), 0 0 0 0 rgba(122, 90, 248, 0); }
}

/* panel */
.saud-root .saud-panel {
  position: fixed; z-index: 1039; width: 384px; height: 620px; display: flex; flex-direction: column; overflow: hidden;
  background: #fff; color: #344054; border-radius: 20px; box-shadow: 0 24px 60px rgba(15, 23, 51, 0.28); border: 1px solid #E3E7ED;
}
.saud-root .saud-head {
  display: flex; align-items: center; gap: 10px; padding: 12px 12px 12px 8px; color: #fff; flex: none;
  background: linear-gradient(135deg, #1D4ED8 0%, #5B4BEA 60%, #7A5AF8 100%);
}
.saud-root .saud-head-av { width: 42px; height: 42px; border-radius: 14px; background: rgba(255, 255, 255, 0.95); display: inline-flex; align-items: center; justify-content: center; flex: none; }
.saud-root .saud-head-av img { width: 32px; height: 32px; }
.saud-root .saud-head-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.saud-root .saud-head-name { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.saud-root .saud-head-name strong { font-size: 16px; font-weight: 800; color: #fff; }
.saud-root .saud-head-text small { font-size: 11.5px; opacity: 0.9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.saud-root .saud-quota { font-size: 10.5px; font-weight: 700; padding: 1px 8px; border-radius: 20px; background: rgba(255, 255, 255, 0.2); white-space: nowrap; }
.saud-root .saud-quota-low { background: #FDB022; color: #3b2600; }
.saud-root .saud-ic { border: 0; background: transparent; color: #fff; width: 34px; height: 34px; border-radius: 10px; cursor: pointer; opacity: 0.85; flex: none; }
.saud-root .saud-ic:hover { background: rgba(255, 255, 255, 0.16); opacity: 1; }
.saud-root .saud-ic:disabled { opacity: 0.4; cursor: default; }

.saud-root .saud-body { flex: 1; overflow-y: auto; padding: 14px 12px 6px; background: #F6F7FB; overscroll-behavior: contain; }
.saud-root .saud-msg { display: flex; gap: 8px; margin-bottom: 12px; align-items: flex-start; }
.saud-root .saud-user { flex-direction: row-reverse; }
.saud-root .saud-user .saud-col { align-items: flex-start; }
.saud-root .saud-av { width: 30px; height: 30px; border-radius: 10px; background: #fff; border: 1px solid #E3E7ED; display: inline-flex; align-items: center; justify-content: center; flex: none; }
.saud-root .saud-av img { width: 22px; height: 22px; }
.saud-root .saud-col { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; min-width: 0; max-width: 86%; }
.saud-root .saud-saud .saud-col { align-items: stretch; }
.saud-root .saud-bubble { padding: 9px 12px; border-radius: 16px; font-size: 13.5px; line-height: 1.75; word-wrap: break-word; overflow-wrap: anywhere; }
.saud-root .saud-bubble ul { margin: 2px 0; padding-right: 18px; padding-left: 0; }
.saud-root .saud-saud .saud-bubble { background: #fff; border: 1px solid #E3E7ED; border-top-right-radius: 6px; color: #1f2937; }
.saud-root .saud-user .saud-bubble { background: #1D4ED8; color: #fff; border-top-left-radius: 6px; margin-right: auto; }
.saud-root .saud-user { justify-content: flex-start; }
.saud-root .saud-user .saud-col { margin-right: auto; margin-left: 0; align-items: flex-end; }
.saud-root .saud-bubble-err { background: #FEF3F2 !important; border-color: #FECDCA !important; color: #B42318 !important; }
.saud-root .saud-tag { font-size: 11px; color: #667085; }
.saud-root .saud-retry { align-self: flex-start; border: 1px solid #FECDCA; background: #fff; color: #B42318; border-radius: 10px; font-size: 12px; font-weight: 700; padding: 4px 10px; cursor: pointer; }

.saud-root .saud-prompts { display: flex; flex-wrap: wrap; gap: 6px; margin: -4px 38px 12px 0; }
.saud-root .saud-chip {
  border: 1px solid #C7D2FE; background: #fff; color: #1D4ED8; border-radius: 20px; padding: 5px 12px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  transition: background 0.12s ease;
}
.saud-root .saud-chip:hover { background: #EEF2FF; }
.saud-root .saud-chip .fa { color: #7A5AF8; margin-left: 3px; }
.saud-root .saud-chip:disabled { opacity: 0.5; cursor: default; }

.saud-root .saud-card { background: #fff; border: 1px solid #E3E7ED; border-radius: 14px; padding: 10px; display: flex; flex-direction: column; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.saud-root .saud-card-row { display: flex; gap: 10px; align-items: center; }
.saud-root .saud-card-ic { width: 38px; height: 38px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; flex: none; font-size: 18px; color: #fff; }
.saud-root .saud-card-excel .saud-card-ic { background: linear-gradient(135deg, #12B76A, #079455); }
.saud-root .saud-card-pdf .saud-card-ic { background: linear-gradient(135deg, #F97066, #D92D20); }
.saud-root .saud-card-image .saud-card-ic { background: linear-gradient(135deg, #9B8AFB, #7A5AF8); }
.saud-root .saud-card-chart .saud-card-ic { background: linear-gradient(135deg, #36BFFA, #0086C9); }
.saud-root .saud-card-open_page .saud-card-ic { background: linear-gradient(135deg, #98A2B3, #667085); }
.saud-root .saud-card-main { min-width: 0; display: flex; flex-direction: column; }
.saud-root .saud-card-main b { font-size: 13.5px; color: #0F1733; }
.saud-root .saud-card-main small { font-size: 11.5px; color: #667085; line-height: 1.6; }
.saud-root .saud-card-prev { width: 100%; max-height: 220px; object-fit: cover; object-position: top; border-radius: 10px; border: 1px solid #E3E7ED; }
.saud-root .saud-card-note { font-size: 12px; line-height: 1.6; }
.saud-root .saud-note-busy { color: #1D4ED8; }
.saud-root .saud-note-done { color: #079455; }
.saud-root .saud-note-error { color: #B42318; }
.saud-root .saud-card-btn {
  border: 0; border-radius: 10px; padding: 7px 12px; font-size: 13px; font-weight: 700; cursor: pointer; color: #fff; background: #1D4ED8;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
}
.saud-root .saud-card-excel .saud-card-btn { background: #079455; }
.saud-root .saud-card-pdf .saud-card-btn { background: #D92D20; }
.saud-root .saud-card-image .saud-card-btn { background: #7A5AF8; }
.saud-root .saud-card-chart .saud-card-btn { background: #0086C9; }
.saud-root .saud-card-open_page .saud-card-btn { background: #475467; }
.saud-root .saud-card-btn:hover { filter: brightness(1.08); }
.saud-root .saud-card-btn:disabled { opacity: 0.65; cursor: default; }

.saud-root .saud-typing { display: inline-flex !important; gap: 4px; align-items: center; padding: 12px 14px !important; align-self: flex-start; }
.saud-root .saud-typing i { width: 7px; height: 7px; border-radius: 50%; background: #7A5AF8; display: block; animation: saud-dot 1.2s infinite ease-in-out; }
.saud-root .saud-typing i:nth-child(2) { animation-delay: 0.15s; }
.saud-root .saud-typing i:nth-child(3) { animation-delay: 0.3s; }
@keyframes saud-dot { 0%, 80%, 100% { opacity: 0.3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-4px); } }
.saud-root .saud-busy-note { font-size: 11.5px; color: #667085; }

.saud-root .saud-foot { flex: none; padding: 8px 12px 10px; border-top: 1px solid #E3E7ED; background: #fff; display: flex; flex-direction: column; gap: 6px; }
.saud-root .saud-limit { font-size: 12px; font-weight: 700; color: #B54708; background: #FFFAEB; border: 1px solid #FEDF89; border-radius: 10px; padding: 6px 10px; }
.saud-root .saud-attach { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 12px; font-weight: 700; color: #475467;
  border: 1px dashed #BFC6D1; border-radius: 20px; padding: 3px 10px; cursor: pointer; user-select: none; }
.saud-root .saud-attach input { margin: 0; }
.saud-root .saud-attach-on { border-style: solid; border-color: #7A5AF8; color: #5B4BEA; background: #F4F3FF; }
.saud-root .saud-input { display: flex; align-items: flex-end; gap: 8px; }
.saud-root .saud-input textarea {
  flex: 1; resize: none; border: 1px solid #BFC6D1; border-radius: 14px; padding: 8px 12px; font-size: 13.5px; line-height: 1.6; max-height: 120px;
  font-family: inherit; color: #1f2937; background: #fff; outline: none;
}
.saud-root .saud-input textarea:focus { border-color: #1D4ED8; box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.15); }
.saud-root .saud-send { width: 42px; height: 42px; border: 0; border-radius: 14px; background: linear-gradient(135deg, #1D4ED8, #7A5AF8); color: #fff; cursor: pointer; flex: none; }
.saud-root .saud-send .fa { transform: scaleX(-1); }
.saud-root .saud-send:disabled { opacity: 0.45; cursor: default; }
.saud-root .saud-privacy { font-size: 10.5px; color: #8A93A6; }

.saud-root .saud-pop-enter-active, .saud-root .saud-pop-leave-active { transition: opacity 0.16s ease, transform 0.16s ease; }
.saud-root .saud-pop-enter, .saud-root .saud-pop-leave-to { opacity: 0; transform: translateY(10px); }

.saud-chart-body { min-height: 320px; }
.saud-chart-modal .apexcharts-canvas { margin: 0 auto; }

/* phones: a full-screen sheet */
@media (max-width: 576px) {
  .saud-root .saud-panel { left: 0 !important; right: 0; top: 0; bottom: 0 !important; width: 100%; height: 100%; max-height: none !important; border-radius: 0; border: 0; }
  .saud-root .saud-head { padding-top: calc(12px + env(safe-area-inset-top)); }
  .saud-root .saud-foot { padding-bottom: calc(10px + env(safe-area-inset-bottom)); }
  .saud-root .saud-fab { width: 54px; height: 54px; }
  .saud-root .saud-fab img { width: 34px; height: 34px; }
  .saud-root .saud-fab-tip { display: none; }
  .saud-root .saud-col { max-width: 88%; }
  .saud-root .saud-input textarea { font-size: 16px; } /* no zoom on iOS */
}
@media (prefers-reduced-motion: reduce) {
  .saud-root .saud-fab { animation: none; }
  .saud-root .saud-pop-enter-active, .saud-root .saud-pop-leave-active { transition: none; }
  .saud-root .saud-typing i { animation: none; opacity: 0.7; }
}
@media print { .saud-root, .saud-root .saud-panel, .saud-root .saud-fab { display: none !important; } }

/* dark skin */
.dark-layout .saud-root .saud-panel { background: #283046; color: #d0d2d6; border-color: #3b4253; }
.dark-layout .saud-root .saud-body { background: #161d31; }
.dark-layout .saud-root .saud-saud .saud-bubble, .dark-layout .saud-root .saud-card, .dark-layout .saud-root .saud-av, .dark-layout .saud-root .saud-chip { background: #283046; border-color: #3b4253; color: #d0d2d6; }
.dark-layout .saud-root .saud-card-main b { color: #fff; }
.dark-layout .saud-root .saud-foot, .dark-layout .saud-root .saud-input textarea { background: #283046; border-color: #3b4253; color: #d0d2d6; }

/* v2: right-side position, export scope, «كل البيانات» progress, designs */
.saud-root.saud-side-right .saud-fab-tip { left: auto; right: calc(100% + 12px); }
.saud-root.saud-side-right .saud-fab-ai { right: auto; left: -6px; }
.saud-root .saud-scope { display: flex; flex-direction: column; gap: 6px; }
.saud-root .saud-scope-q { font-size: 11.5px; color: #475467; font-weight: 700; }
.saud-root .saud-scope-btns { display: flex; gap: 6px; }
.saud-root .saud-scope-btns .saud-card-btn { flex: 1; padding: 7px 6px; font-size: 12.5px; }
.saud-root .saud-card .saud-card-btn.saud-scope-page { background: #fff; color: #344054; border: 1px solid #BFC6D1; }
.saud-root .saud-card .saud-card-btn.saud-scope-page:hover { background: #F2F4F7; filter: none; }
.saud-root .saud-prog-row { display: flex; align-items: center; gap: 8px; }
.saud-root .saud-prog { flex: 1; height: 8px; border-radius: 6px; background: #EEF1F4; overflow: hidden; position: relative; }
.saud-root .saud-prog i { display: block; height: 100%; width: 0; border-radius: 6px; background: linear-gradient(90deg, #1D4ED8, #7A5AF8); transition: width 0.2s ease; }
.saud-root .saud-prog-indet i { width: 35%; position: absolute; animation: saud-indet 1.1s ease-in-out infinite; }
@keyframes saud-indet { 0% { left: -35%; } 100% { left: 100%; } }
.saud-root .saud-cancel { border: 1px solid #FECDCA; background: #fff; color: #B42318; border-radius: 10px; font-size: 12px; font-weight: 700; padding: 3px 10px; cursor: pointer; flex: none; }
.saud-root .saud-card-design .saud-card-ic { background: linear-gradient(135deg, #F670C7, #DD2590); }
.saud-root .saud-card-design .saud-card-btn { background: #C11574; }
.dark-layout .saud-root .saud-card .saud-card-btn.saud-scope-page { background: #283046; color: #d0d2d6; border-color: #3b4253; }
/* rtl:end:ignore */
</style>
